import type { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Calendar } from "lucide-react";
import { cn } from "@/modern/utils";
import type { Accent } from "../types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modern/components/ui/select";
import {
  USERS_CHIP_CLS,
  USERS_EMPTY_CLS,
  USERS_HELPER_CLS,
  USERS_HERO_METRIC_CLS,
  USERS_META_CLS,
  USERS_SUBTITLE_CLS,
  USERS_TITLE_CLS,
} from "../constants/typography";
import { getRoleIcon } from "../constants/roles";

export const USERS_PERIOD_TRIGGER_CLS = cn(
  "!h-8 h-8 py-0 shrink-0 gap-1.5 rounded-md px-2.5",
  "border border-border/60 bg-muted/30 font-normal text-sm text-foreground",
  "shadow-none ring-offset-0",
  "hover:bg-muted/50 hover:border-border/80",
  "focus:ring-1 focus:ring-ring focus:ring-offset-0",
  "data-[state=open]:border-border data-[state=open]:bg-muted/40",
  "[&>span]:min-w-0 [&>span]:truncate [&>span]:font-normal [&>span]:text-left",
  "[&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:opacity-60",
);

export const USERS_PERIOD_TRIGGER_GHOST_CLS = cn(
  "!h-7 h-7 py-0 shrink-0 gap-1.5 rounded-md px-2",
  "border-transparent bg-transparent font-normal text-sm text-muted-foreground",
  "shadow-none ring-offset-0",
  "hover:bg-muted/50 hover:text-foreground hover:border-transparent",
  "focus:ring-1 focus:ring-ring focus:ring-offset-0 focus:ring-offset-0",
  "data-[state=open]:bg-muted/50 data-[state=open]:text-foreground data-[state=open]:border-transparent",
  "[&>span]:min-w-0 [&>span]:truncate [&>span]:font-normal",
  "[&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:opacity-60",
);

export function UsersPeriodSelect<T extends string>({
  value,
  onValueChange,
  options,
  className,
  triggerClassName,
  variant = "default",
}: {
  value: T;
  onValueChange: (v: T) => void;
  options: { value: T; label: string }[];
  className?: string;
  triggerClassName?: string;
  variant?: "default" | "ghost";
}) {
  return (
    <Select value={value} onValueChange={(v) => onValueChange(v as T)}>
      <SelectTrigger
        className={cn(
          variant === "ghost" ? USERS_PERIOD_TRIGGER_GHOST_CLS : USERS_PERIOD_TRIGGER_CLS,
          triggerClassName,
        )}
      >
        <Calendar className="size-3.5 shrink-0 opacity-70" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={cn("min-w-[9rem]", className)}>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} className="text-sm font-normal">
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export const accentText: Record<Accent, string> = {
  primary: "text-primary",
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
  teal: "text-[hsl(var(--chart-2))]",
  amber: "text-[hsl(var(--chart-4))]",
  violet: "text-[hsl(var(--chart-4))]",
  neutral: "text-muted-foreground",
};

export const accentChipBg: Record<Accent, string> = {
  primary: "bg-primary/10 text-primary ring-1 ring-inset ring-primary/20",
  success: "bg-success/10 text-success ring-1 ring-inset ring-success/20",
  warning: "bg-warning/10 text-warning ring-1 ring-inset ring-warning/20",
  destructive: "bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20",
  teal: "bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] ring-1 ring-inset ring-[hsl(var(--chart-2))]/20",
  amber: "bg-[hsl(var(--chart-4))]/10 text-[hsl(var(--chart-4))] ring-1 ring-inset ring-[hsl(var(--chart-4))]/20",
  violet: "bg-[hsl(var(--chart-4))]/10 text-[hsl(var(--chart-4))] ring-1 ring-inset ring-[hsl(var(--chart-4))]/20",
  neutral: "bg-secondary text-foreground/70 ring-1 ring-inset ring-border/60",
};

export const accentBar: Record<Accent, string> = {
  primary: "bg-gradient-to-b from-primary/70 via-primary/50 to-primary/10",
  success: "bg-gradient-to-b from-success/70 via-success/50 to-success/10",
  warning: "bg-gradient-to-b from-warning/70 via-warning/50 to-warning/10",
  destructive: "bg-gradient-to-b from-destructive/70 via-destructive/50 to-destructive/10",
  teal: "bg-gradient-to-b from-[hsl(var(--chart-2))]/70 via-[hsl(var(--chart-2))]/50 to-[hsl(var(--chart-2))]/10",
  amber: "bg-gradient-to-b from-[hsl(var(--chart-4))]/70 via-[hsl(var(--chart-4))]/50 to-[hsl(var(--chart-4))]/10",
  violet: "bg-gradient-to-b from-[hsl(var(--chart-4))]/70 via-[hsl(var(--chart-4))]/50 to-[hsl(var(--chart-4))]/10",
  neutral: "bg-gradient-to-b from-primary/40 via-primary/20 to-transparent",
};

export const accentGlow: Record<Accent, string> = {
  primary: "before:bg-[radial-gradient(120%_60%_at_0%_0%,hsl(var(--primary)/0.08),transparent_60%)]",
  success: "before:bg-[radial-gradient(120%_60%_at_0%_0%,hsl(var(--success)/0.08),transparent_60%)]",
  warning: "before:bg-[radial-gradient(120%_60%_at_0%_0%,hsl(var(--warning)/0.08),transparent_60%)]",
  destructive: "before:bg-[radial-gradient(120%_60%_at_0%_0%,hsl(var(--destructive)/0.08),transparent_60%)]",
  teal: "before:bg-[radial-gradient(120%_60%_at_0%_0%,hsl(var(--chart-2)/0.08),transparent_60%)]",
  amber: "before:bg-[radial-gradient(120%_60%_at_0%_0%,hsl(var(--chart-4)/0.08),transparent_60%)]",
  violet: "before:bg-[radial-gradient(120%_60%_at_0%_0%,hsl(var(--chart-4)/0.08),transparent_60%)]",
  neutral: "before:bg-transparent",
};

export function AccentCard({
  title,
  subtitle,
  icon,
  accent = "neutral",
  action,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  accent?: Accent;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative rounded-xl border border-border/70 bg-card overflow-hidden",
        "shadow-[inset_0_1px_0_hsl(var(--foreground)/0.04),0_1px_3px_hsl(0_0%_0%/0.18)]",
        "after:content-[''] after:absolute after:inset-0 after:pointer-events-none",
        accentGlow[accent].replace("before:", "after:"),
        className,
      )}
    >
      <span
        aria-hidden
        className={cn("absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full z-10", accentBar[accent])}
      />
      <header className="relative flex items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          {icon && (
            <div
              className={cn(
                "size-9 rounded-lg flex items-center justify-center shrink-0",
                accentChipBg[accent],
              )}
            >
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <h3 className={cn(USERS_TITLE_CLS, "leading-tight tracking-tight")}>
              {title}
            </h3>
            {subtitle && (
              <p className={cn(USERS_SUBTITLE_CLS, "mt-0.5 truncate")}>{subtitle}</p>
            )}
          </div>
        </div>
        {action}
      </header>
      <div className="relative px-5 pb-5">{children}</div>
    </section>
  );
}

export function ViewAllLink({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center gap-1 rounded-md px-1.5",
        "text-sm font-medium text-primary",
        "hover:bg-primary/10 hover:text-primary transition-colors",
        "group/link",
      )}
    >
      View all
      <ArrowUpRight className="size-3.5 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
    </button>
  );
}

export function Empty({ note }: { note: string }) {
  return (
    <div className={cn("rounded-xl border border-dashed border-muted-foreground/30 p-12 text-center", USERS_EMPTY_CLS)}>
      {note}
    </div>
  );
}

export function TabEmpty({
  icon,
  message,
  hint,
}: {
  icon: string;
  message: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center">
      <div className="flex size-10 items-center justify-center rounded-xl border border-border/60 bg-muted/40 text-muted-foreground/60">
        <i className={cn(icon, "text-base")} aria-hidden />
      </div>
      <div className="space-y-1">
        <p className={cn(USERS_TITLE_CLS, "font-normal text-muted-foreground")}>{message}</p>
        {hint && <p className={USERS_HELPER_CLS}>{hint}</p>}
      </div>
    </div>
  );
}

/** Short purpose line for a Users tab summary card or page intro. */
export function UsersTabDescription({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn(USERS_HELPER_CLS, "mt-2 max-w-prose leading-relaxed text-muted-foreground/90", className)}>
      {children}
    </p>
  );
}

export function Kpi({
  label,
  value,
  sub,
  delta,
  icon,
  tone = "neutral",
}: {
  label: string;
  value: string;
  sub?: string;
  delta?: ReactNode;
  icon?: ReactNode;
  tone?: "neutral" | "primary" | "warning" | "success";
}) {
  const valueColor =
    tone === "warning"
      ? "text-warning"
      : tone === "primary"
        ? "text-primary"
        : tone === "success"
          ? "text-success"
          : "text-foreground";

  const iconColor =
    tone === "warning"
      ? "text-warning"
      : tone === "primary"
        ? "text-primary"
        : tone === "success"
          ? "text-success"
          : "text-muted-foreground";

  return (
    <div
      className={cn(
        "rounded-xl border border-border/60 bg-card px-3.5 py-3",
        "shadow-[0_1px_0_hsl(var(--foreground)/0.03)]",
        "transition-[border-color,box-shadow] duration-150 ease-out",
        "hover:border-border",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <p className={cn(USERS_CHIP_CLS, "text-muted-foreground leading-none")}>{label}</p>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className={cn(USERS_HERO_METRIC_CLS, valueColor)}>
              {value}
            </span>
            {delta}
          </div>
          {sub ? (
            <p className={cn("truncate leading-none", USERS_HELPER_CLS)}>{sub}</p>
          ) : null}
        </div>
        {icon && <span className={cn("shrink-0 opacity-80 leading-none", iconColor)}>{icon}</span>}
      </div>
    </div>
  );
}

export function AvgDelta({
  value,
  avg,
}: {
  value: number;
  avg: number;
}) {
  if (!avg) return null;
  const diff = value - avg;
  if (diff === 0)
    return (
      <span className={cn("inline-flex items-center h-6 px-2 rounded-md bg-secondary text-muted-foreground", USERS_META_CLS)}>
        on avg
      </span>
    );
  const pct = Math.round((diff / avg) * 100);
  const up = pct > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 h-6 px-2 rounded-md",
        USERS_CHIP_CLS,
        "tabular-nums",
        up ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive",
      )}
    >
      {up ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
      {Math.abs(pct)}%
    </span>
  );
}

export function DeltaValue({ value }: { value: number }) {
  if (value === 0)
    return (
      <span className={cn("inline-flex items-center h-6 px-2 rounded-md bg-secondary text-muted-foreground", USERS_META_CLS)}>
        —
      </span>
    );
  const up = value > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 h-6 px-2 rounded-md",
        USERS_CHIP_CLS,
        "tabular-nums",
        up ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive",
      )}
    >
      {up ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
      {Math.abs(value)}
    </span>
  );
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function StatusPip({ user }: { user: any }) {
  if (user.isDeactivated)
    return (
      <span className={cn(USERS_CHIP_CLS, "text-foreground/70 bg-secondary px-1.5 py-0.5 rounded")}>
        Deactivated
      </span>
    );
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function StatusPill({ user }: { user: any }) {
  const cls = cn("inline-flex items-center gap-1.5 px-2 h-6 rounded-md", USERS_CHIP_CLS);
  if (user.isDeactivated)
    return <span className={cn(cls, "bg-secondary text-foreground/70")}>Deactivated</span>;
  return null;
}

function pickPalette(key: string, palettes: string[]) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return palettes[Math.abs(h) % palettes.length];
}

const rolePalettes: Record<string, string> = {
  Administrator: "bg-[hsl(var(--chart-1)/0.12)] text-[hsl(var(--chart-1))]",
  Executive:     "bg-primary/10 text-primary",
  Office:        "bg-[hsl(var(--chart-2)/0.12)] text-[hsl(var(--chart-2))]",
  Scheduler:     "bg-[hsl(var(--chart-4)/0.12)] text-[hsl(var(--chart-4))]",
  Workforce:     "bg-warning/10 text-warning",
  Deactivated:   "bg-destructive/10 text-destructive",
};
const roleFallback = [
  "bg-primary/10 text-primary",
  "bg-destructive/10 text-destructive",
  "bg-warning/10 text-warning",
  "bg-success/10 text-success",
  "bg-[hsl(var(--chart-2)/0.12)] text-[hsl(var(--chart-2))]",
  "bg-[hsl(var(--chart-3)/0.12)] text-[hsl(var(--chart-3))]",
  "bg-[hsl(var(--chart-4)/0.12)] text-[hsl(var(--chart-4))]",
  "bg-[hsl(var(--chart-5)/0.12)] text-[hsl(var(--chart-5))]",
];

const teamPalettes: Record<string, string> = {
  HQ: "bg-primary/10 text-primary",
  "Crew A": "bg-warning/10 text-warning",
  "Crew B": "bg-[hsl(var(--chart-2)/0.12)] text-[hsl(var(--chart-2))]",
  "Crew C": "bg-destructive/10 text-destructive",
};
const teamFallback = [
  "bg-primary/10 text-primary",
  "bg-warning/10 text-warning",
  "bg-success/10 text-success",
  "bg-destructive/10 text-destructive",
];

export function RoleBadge({ role, isDeactivated }: { role: string; isDeactivated?: boolean }) {
  const cls = isDeactivated
    ? rolePalettes.Deactivated
    : (rolePalettes[role] ?? pickPalette(role, roleFallback));
  const RoleIcon = getRoleIcon(role);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 h-6 rounded-md",
        USERS_CHIP_CLS,
        cls,
      )}
    >
      <RoleIcon className="size-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
      {role}
    </span>
  );
}

export function TeamBadge({ team }: { team?: string | null }) {
  if (!team) return null;
  const cls = teamPalettes[team] ?? pickPalette(team, teamFallback);
  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 h-6 rounded-md whitespace-nowrap shrink-0",
        USERS_CHIP_CLS,
        cls,
      )}
    >
      {team}
    </span>
  );
}

export function ListRowStats({
  resourceCount,
  pointsTotal,
  scheduledHours,
  taskCount,
  isDeactivated,
  compact = false,
}: {
  resourceCount: number;
  pointsTotal: number;
  scheduledHours?: number;
  taskCount?: number;
  isDeactivated?: boolean;
  /** Sidebar roster — inline meta (resources · points · tasks) */
  compact?: boolean;
}) {
  if (isDeactivated) return null;
  const hours = scheduledHours ?? 0;
  const hoursLabel = hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
  const tasks = taskCount ?? 0;

  if (compact) {
    const cells = [
      {
        key: "resources",
        icon: "fa-screwdriver-wrench",
        value: String(resourceCount),
        tipTitle: "Assigned resources",
        tipBody: `${resourceCount} on this board`,
      },
      {
        key: "points",
        icon: "fa-award",
        value: pointsTotal.toLocaleString(),
        tipTitle: "Points awarded",
        tipBody: `${pointsTotal.toLocaleString()} total`,
      },
      {
        key: "tasks",
        icon: "fa-list-check",
        value: String(tasks),
        tipTitle: "Tasks scheduled",
        tipBody: `${tasks} scheduled on the board`,
      },
    ] as const;

    return (
      <div className="flex flex-wrap items-center justify-end gap-x-2.5 gap-y-1 text-sm text-muted-foreground">
        {cells.map((cell, i) => (
          <span key={cell.key} className="inline-flex items-center gap-2.5">
            {i > 0 ? (
              <span className="text-border-enhanced" aria-hidden>
                ·
              </span>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 cursor-default">
                  <i className={cn("fa-light text-sm opacity-70", cell.icon)} />
                  <span className="tabular-nums text-foreground/90">{cell.value}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-popover text-popover-foreground border border-border shadow-md">
                <p className="font-medium">{cell.tipTitle}</p>
                <p className="text-muted-foreground">{cell.tipBody}</p>
              </TooltipContent>
            </Tooltip>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/15 text-sm cursor-default">
            <i className="fa-light fa-screwdriver-wrench text-sm opacity-80" />
            <span className="tabular-nums font-medium">{resourceCount}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-popover text-popover-foreground border border-border shadow-md">
          <div className="space-y-0.5">
            <p className="font-medium">Assigned resources</p>
            <p className="text-muted-foreground">{resourceCount} on this board</p>
          </div>
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-[hsl(var(--chart-4)/0.12)] text-[hsl(var(--chart-4))] ring-1 ring-inset ring-[hsl(var(--chart-4)/0.2)] text-sm cursor-default">
            <i className="fa-light fa-award text-sm opacity-80" />
            <span className="tabular-nums font-medium">
              {pointsTotal.toLocaleString()}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-popover text-popover-foreground border border-border shadow-md">
          <div className="space-y-0.5">
            <p className="font-medium">Points awarded</p>
            <p className="text-muted-foreground">{pointsTotal.toLocaleString()} total</p>
          </div>
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-success/10 text-success ring-1 ring-inset ring-success/15 text-sm cursor-default">
            <i className="fa-light fa-clock text-sm opacity-80" />
            <span className="tabular-nums font-medium">{hoursLabel}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-popover text-popover-foreground border border-border shadow-md">
          <div className="space-y-0.5">
            <p className="font-medium">Scheduled hours</p>
            <p className="text-muted-foreground">{hoursLabel} on the board</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
